import React, { createContext, useContext, useState, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';

interface ErrorContextType {
  errorMessage: string;
  setGlobalError: (msg: string) => void;
}

const FALLBACK_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const MAX_ERROR_LENGTH = 160;
const SAFE_CHARACTERS_REGEX = /^[\w\s.,'"!?@:/\-\(\)&%$#]+$/i;

const ErrorContext = createContext<ErrorContextType>({
  errorMessage: '',
  setGlobalError: () => {},
});

const normalizeErrorMessage = (message?: string | null): string => {
  const trimmed = message?.trim();
  if (!trimmed) {
    return FALLBACK_ERROR_MESSAGE;
  }
  if (
    trimmed.length <= MAX_ERROR_LENGTH &&
    SAFE_CHARACTERS_REGEX.test(trimmed)
  ) {
    return trimmed;
  }
  console.error('[GlobalError]', trimmed);
  return FALLBACK_ERROR_MESSAGE;
};

export const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [errorMessage, setErrorMessage] = useState('');

  const setGlobalError = (message: string) => {
    setErrorMessage(normalizeErrorMessage(message));
  };

  useEffect(() => {
    if (errorMessage) {
      const timeout = setTimeout(() => setErrorMessage(''), 5000);
      return () => clearTimeout(timeout);
    }
  }, [errorMessage]);

  return (
    <ErrorContext.Provider value={{ errorMessage, setGlobalError }}>
      {children}
      {errorMessage && (
        <Snackbar
          open={true}
          autoHideDuration={5000}
          onClose={() => setErrorMessage('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Alert onClose={() => setErrorMessage('')} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </ErrorContext.Provider>
  );
};

export const useErrorContext = () => useContext(ErrorContext);
